// Reads an uploaded file, works out what it is (Monday board, Google Contacts, LinkedIn, conference list, touch
// log, past deals, participation), which row holds the headers, and which column holds which field. Peter can
// correct any of it on the Sources page before the rows are stored. Nothing here touches the network.

import type { SourceKind } from "../../shared/network";
import { FIELDS, KIND_LABEL, REQUIRED, type CanonRow } from "../../shared/sources";
import type { MondayItem } from "../lib/api";
import { parseCsv } from "../lib/csv";
import { readXlsx } from "../lib/xlsx-read";

export interface ParsedSheet {
  name: string;
  rows: string[][];
}
export interface ParsedFile {
  filename: string;
  sheets: ParsedSheet[];
}

export interface Detection {
  kind: SourceKind;
  confidence: number; // 0-1; under 0.5 the Sources page should ask Peter to confirm the kind
  sheet: number;
  headerRow: number;
  headers: string[];
  /** field key -> column index, or null when no column matched */
  mapping: Record<string, number | null>;
  /** Labels of required fields that have no column yet. Empty means the source can be stored. */
  missing: string[];
  dataRows: number;
  /** Text above the header row: a Monday board name, a conference title, a LinkedIn notice. */
  title: string;
  /** Conference lists: the event, from the title rows or the file name. date is "" when unknown (ask Peter). */
  event?: { name: string; date: string };
}

const KINDS = Object.keys(FIELDS) as SourceKind[];
const cellText = (v: unknown): string => (v == null ? "" : typeof v === "number" ? (Number.isInteger(v) ? String(v) : String(+v.toFixed(6))) : String(v)).trim();

/** Reads .xlsx, .csv, .tsv or .txt. Old binary .xls and Numbers files get a clear error. */
export async function readFile(file: { name: string; arrayBuffer(): Promise<ArrayBuffer> }): Promise<ParsedFile> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const isZip = buf[0] === 0x50 && buf[1] === 0x4b && buf[2] === 0x03 && buf[3] === 0x04;
  const isOle = buf[0] === 0xd0 && buf[1] === 0xcf && buf[2] === 0x11 && buf[3] === 0xe0;
  if (isOle) throw new Error(`${file.name} is an old .xls file. Open it in Excel and save it as .xlsx or CSV, then drop it here again.`);
  if (isZip) {
    if (/\.numbers$/i.test(file.name)) throw new Error(`${file.name} is a Numbers file. Export it to Excel or CSV first.`);
    const sheets = await readXlsx(buf);
    return { filename: file.name, sheets: sheets.map((s) => ({ name: s.name, rows: s.rows.map((r) => r.map(cellText)) })) };
  }
  let text = new TextDecoder("utf-8").decode(buf);
  if (text.includes("�")) text = new TextDecoder("windows-1252").decode(buf); // Excel's "CSV" on Windows
  const rows = parseCsv(text).map((r) => r.map((c) => c.trim()));
  return { filename: file.name, sheets: [{ name: file.name.replace(/\.[^.]+$/, ""), rows }] };
}

const filled = (r: string[] | undefined) => (r ? r.filter((c) => c !== "").length : 0);

/** Best column for each field: specific header names win over generic ones, and no column is used twice. */
export function mapColumns(kind: SourceKind, headers: string[]): Record<string, number | null> {
  const fields = FIELDS[kind];
  const out: Record<string, number | null> = {};
  for (const f of fields) out[f.key] = null;
  const used = new Set<number>();
  const depth = Math.max(...fields.map((f) => f.hints.length));
  const norm = headers.map((h) => h.replace(/\s+/g, " ").trim());
  for (let level = 0; level < depth; level++) {
    for (const f of fields) {
      if (out[f.key] != null) continue;
      const rx = f.hints[level];
      if (!rx) continue;
      const i = norm.findIndex((h, idx) => h !== "" && !used.has(idx) && rx.test(h));
      if (i >= 0) {
        out[f.key] = i;
        used.add(i);
      }
    }
  }
  return out;
}

const SIGNATURES: Record<SourceKind, [RegExp, number][]> = {
  crm: [[/^investor type$/i, 3], [/check size/i, 2], [/^relationship/i, 2], [/^subitems$/i, 3], [/^item id$/i, 3], [/how we met/i, 2], [/last contacted/i, 2], [/^owner$/i, 1]],
  contacts: [[/^given name$/i, 3], [/^family name$/i, 3], [/^e-mail 1 - value$/i, 4], [/^organization 1 - name$/i, 3], [/^group membership$/i, 3], [/^labels$/i, 2]],
  linkedin: [[/^connected on$/i, 6], [/^url$/i, 1], [/^position$/i, 1], [/^email address$/i, 1]],
  conference: [[/^(attendee|registrant|delegate)( name)?$/i, 4], [/registration type|ticket type|badge type|attendee type/i, 4]],
  touches: [[/^direction$/i, 4], [/^channel$/i, 2], [/^summary$/i, 1], [/^date$/i, 1]],
  deals: [[/^raise/i, 3], [/^launch/i, 3], [/^deal[_ ]?id$/i, 1], [/^sponsor$/i, 1], [/^close/i, 1]],
  participation: [[/^outcome$/i, 4], [/^amount$/i, 1], [/^deal[_ ]?id$/i, 2], [/pitched/i, 3]],
};

function missingFields(kind: SourceKind, mapping: Record<string, number | null>): string[] {
  const label = (k: string) => FIELDS[kind].find((f) => f.key === k)?.label ?? k;
  return REQUIRED[kind].filter((group) => group.every((k) => mapping[k] == null)).map((group) => group.map(label).join(" or "));
}

function scoreKind(kind: SourceKind, headers: string[], filename: string): number {
  const mapping = mapColumns(kind, headers);
  let s = Object.values(mapping).filter((v) => v != null).length;
  for (const [rx, w] of SIGNATURES[kind]) if (headers.some((h) => rx.test(h))) s += w;
  if (kind === "conference" && /attendee|registr|conference|summit|forum|expo|event|delegates/i.test(filename)) s += 3;
  if (kind === "linkedin" && /linkedin|connections/i.test(filename)) s += 3;
  if (kind === "contacts" && /google|contacts/i.test(filename)) s += 2;
  if (kind === "crm" && /monday|board|crm|investor/i.test(filename)) s += 2;
  if (kind === "touches" && /interaction|touch|activity|log|calls/i.test(filename)) s += 2;
  if (missingFields(kind, mapping).length) s -= 6;
  return s;
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
/** Finds a date in free text: 2026-09-22, 9/22/2026, Sep 22, 2026 or 22 Sep 2026. */
export function findDate(text: string): string {
  let m = /\b(20\d{2})-(\d{2})-(\d{2})\b/.exec(text);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /\b(\d{1,2})\/(\d{1,2})\/(20\d{2})\b/.exec(text);
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  m = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.? (\d{1,2}),? (20\d{2})\b/i.exec(text);
  if (m) return `${m[3]}-${String(MONTHS.indexOf(m[1].toLowerCase()) + 1).padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  m = /\b(\d{1,2}) (jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]* (20\d{2})\b/i.exec(text);
  if (m) return `${m[3]}-${String(MONTHS.indexOf(m[2].toLowerCase()) + 1).padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return "";
}

function eventFrom(title: string, filename: string): { name: string; date: string } {
  const date = findDate(title) || findDate(filename.replace(/[_]/g, "-"));
  let name = title.split(/\n/)[0] ?? "";
  name = name.split(/\s+[-–|]\s+(?=.*(attendee|registr|list|date|roster))/i)[0].replace(/\b(event )?date:?\s*\S+$/i, "").trim();
  if (!name || /^event date/i.test(name)) {
    name = filename
      .replace(/\.[^.]+$/, "")
      .replace(/[_-]+/g, " ")
      .replace(/\b(attendees?|registered|registrants?|list|export|final)\b/gi, "")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return { name, date };
}

/** Picks the sheet, header row and kind that fit best. Pass forceKind when Peter (or the Monday pull) already knows. */
export function detect(pf: ParsedFile, forceKind?: SourceKind): Detection {
  let best: { sheet: number; row: number; kind: SourceKind; score: number } | null = null;
  pf.sheets.forEach((sheet, si) => {
    const limit = Math.min(sheet.rows.length, 40);
    for (let r = 0; r < limit; r++) {
      const headers = sheet.rows[r] ?? [];
      if (filled(headers) < 2) continue;
      for (const kind of forceKind ? [forceKind] : KINDS) {
        const score = scoreKind(kind, headers, pf.filename);
        if (!best || score > best.score) best = { sheet: si, row: r, kind, score };
      }
    }
  });
  if (!best) throw new Error(`${pf.filename} has no header row I can read.`);
  const b = best as { sheet: number; row: number; kind: SourceKind; score: number };
  const sheet = pf.sheets[b.sheet];
  const headers = sheet.rows[b.row].map((h) => h.trim());
  const mapping = mapColumns(b.kind, headers);
  const title = sheet.rows
    .slice(0, b.row)
    .map((r) => r.filter(Boolean).join(" "))
    .filter(Boolean)
    .join("\n");
  const det: Detection = {
    kind: b.kind,
    confidence: Math.max(0, Math.min(1, b.score / 14)),
    sheet: b.sheet,
    headerRow: b.row,
    headers,
    mapping,
    missing: missingFields(b.kind, mapping),
    dataRows: 0,
    title: b.kind === "linkedin" ? "" : title,
  };
  if (b.kind === "conference") det.event = eventFrom(title, pf.filename);
  det.dataRows = normalize(pf, det).length;
  return det;
}

/** Re-runs the column guess after Peter changes the kind. */
export function withKind(pf: ParsedFile, det: Detection, kind: SourceKind): Detection {
  return detect({ filename: pf.filename, sheets: [pf.sheets[det.sheet]] }, kind);
}

/** After Peter edits the mapping by hand. */
export function withMapping(pf: ParsedFile, det: Detection, mapping: Record<string, number | null>): Detection {
  const next = { ...det, mapping, missing: missingFields(det.kind, mapping) };
  next.dataRows = normalize(pf, next).length;
  return next;
}

function splitName(row: CanonRow) {
  const name = (row.name ?? "").replace(/\s+/g, " ").trim();
  if (name && !row.first && !row.last) {
    if (name.includes(",")) {
      const [last, first] = name.split(",", 2).map((s) => s.trim());
      row.first = first;
      row.last = last;
      row.name = `${first} ${last}`.trim();
    } else {
      const parts = name.split(" ");
      row.first = parts[0];
      row.last = parts.slice(1).join(" ");
    }
  } else if (!name && (row.first || row.last)) {
    row.name = `${row.first ?? ""} ${row.last ?? ""}`.trim();
  }
}

/** The mapped rows, ready to store. Monday group titles, repeated header rows and blank rows are handled here. */
export function normalize(pf: ParsedFile, det: Detection): CanonRow[] {
  const rows = pf.sheets[det.sheet].rows;
  const out: CanonRow[] = [];
  const entries = Object.entries(det.mapping).filter(([, i]) => i != null) as [string, number][];
  const sameAsHeader = (r: string[]) => r[0] === det.headers[0] && (r[1] ?? "") === (det.headers[1] ?? "") && filled(r) > 1;
  let group = "";
  for (let i = det.headerRow + 1; i < rows.length; i++) {
    const r = rows[i] ?? [];
    if (!filled(r)) continue;
    if (sameAsHeader(r)) continue;
    // A row with one cell, followed by a header row, is a Monday group title ("Family Offices").
    if (det.kind === "crm" && filled(r) === 1 && r[0] && i + 1 < rows.length && sameAsHeader(rows[i + 1] ?? [])) {
      group = r[0];
      continue;
    }
    const row: CanonRow = {};
    for (const [key, col] of entries) {
      const v = (r[col] ?? "").trim();
      if (v) row[key] = v;
    }
    if (det.kind === "crm" && group && !row.group) row.group = group;
    if (["crm", "contacts", "linkedin", "conference"].includes(det.kind)) splitName(row);
    const hasId =
      det.kind === "deals" ? !!row.dealId : det.kind === "participation" ? !!row.dealId && !!(row.email || row.name) : det.kind === "touches" ? !!row.date && !!(row.email || row.name) : !!(row.name || row.email);
    if (hasId) out.push(row);
  }
  // The group of the first block sits above the header row.
  if (det.kind === "crm") {
    const above = rows.slice(0, det.headerRow).filter((r) => filled(r) === 1 && r[0]);
    const firstGroup = above.length >= 2 ? above[above.length - 1][0] : "";
    if (firstGroup) for (const row of out) if (!row.group) row.group = firstGroup;
  }
  return out;
}

/** The first few data rows, for the mapping table's sample column. */
export function sampleRows(pf: ParsedFile, det: Detection, n = 3): string[][] {
  const rows = pf.sheets[det.sheet].rows;
  const out: string[][] = [];
  for (let i = det.headerRow + 1; i < rows.length && out.length < n; i++) {
    const r = rows[i] ?? [];
    if (filled(r) > 1 && r[0] !== det.headers[0]) out.push(r);
  }
  return out;
}

/** A label for the stored source, e.g. "Monday board: Investor Network - Peter B." */
export function sourceLabel(det: Detection, filename: string): string {
  if (det.kind === "conference" && det.event?.name) return det.event.name;
  if (det.kind === "crm" && det.title) return `Monday board: ${det.title.split("\n")[0]}`;
  return `${KIND_LABEL[det.kind]}: ${filename}`;
}

/** Turns a Monday API pull into the same shape as Monday's "Export to Excel", so one code path maps both. */
export function mondayToParsed(boardName: string, columns: { id: string; title: string }[], items: MondayItem[]): ParsedFile {
  const cols = columns.filter((c) => c.id !== "name");
  const header = ["Name", "Group", ...cols.map((c) => c.title), "Item ID"];
  const rows: string[][] = [[boardName], [], header];
  for (const it of items) {
    const byId = new Map(it.column_values.map((cv) => [cv.id, cv.text ?? ""]));
    rows.push([it.name, it.group?.title ?? "", ...cols.map((c) => byId.get(c.id) ?? ""), it.id]);
  }
  return { filename: `monday-${boardName}.json`, sheets: [{ name: boardName, rows }] };
}
